// 引入 Day.js
import dayjs from 'dayjs';
import i18next from 'i18next';

/**
 * 时长格式化函数
 * @param seconds 秒数
 * @returns 格式化后的时长字符串
 */
/**
 * 时长格式化函数
 * @param seconds 秒数
 * @returns 格式化后的时长字符串
 */
export const formatDuration = (seconds?: number): string => {
  // 处理非数字或负数
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
    return '0s';
  }

  // 如果低于1分钟，返回 xxx s
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  }

  // 如果低于1小时，返回 xxx min xx s
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  }

  // 如果低于1天，返回 xxx h xx min
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const remainingSeconds = Math.floor(seconds % 3600);
    const minutes = Math.floor(remainingSeconds / 60);
    return `${hours}h ${minutes}m`;
  }

  // 如果低于1年，返回 xx D xx h
  if (seconds < 31536000) {
    const days = Math.floor(seconds / 86400);
    const remainingSeconds = Math.floor(seconds % 86400);
    const hours = Math.floor(remainingSeconds / 3600);
    return `${days}D ${hours}h`;
  }

  // 超过1年，返回 xx Y xx D
  const years = Math.floor(seconds / 31536000);
  const remainingSeconds = Math.floor(seconds % 31536000);
  const days = Math.floor(remainingSeconds / 86400);
  return `${years}Y ${days}D`;
};

/**
 * 日期格式化
 * @param time
 * @returns
 */
export const formatTimeAgo = (time?: string): string => {
  if (!time) {
    return '-';
  }
  const t = i18next.t;
  // 获取当前时间
  const now = dayjs();
  const lastLogin = dayjs(time);
  const seconds = Math.abs(now.diff(lastLogin, 'second'));
  if (seconds < 60) {
    return t('time.secondsAgo', { count: seconds });
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return t('time.minutesAgo', { count: minutes });
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return t('time.hoursAgo', { count: hours });
  }
  const days = Math.floor(seconds / 86400);
  return t('time.daysAgo', { count: days });
};

/**
 * 消息通知时间格式化
 * <1分钟: 刚刚 | 1~59分钟: X分钟前 | 1~23小时: X小时前 | 1~7天: X天前 | >7天: YYYY-MM-DD
 */
export const formatCoMessageTime = (time?: string): string => {
  if (!time) return '-';
  const now = dayjs();
  const target = dayjs(time);
  const seconds = Math.abs(now.diff(target, 'second'));
  const t = i18next.t;

  if (seconds < 60) {
    return t('time.justNow');
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return t('time.minutesAgo', { count: minutes });
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return t('time.hoursAgo', { count: hours });
  }
  const days = Math.floor(seconds / 86400);
  if (days <= 7) {
    return t('time.daysAgo', { count: days });
  }
  return target.format('YYYY-MM-DD');
};

/**
 * 数字格式化函数
 * @param num 要格式化的数字
 * @returns 格式化后的字符串
 */
export const formatNumber = (num: number): string => {
  // 处理非数字或负数
  if (typeof num !== 'number' || isNaN(num) || num < 0) {
    return '0';
  }

  // 小于10万，使用千分符分隔
  if (num < 100000) {
    return num.toLocaleString();
  }

  // 10万到100万之间，格式化为 xx.xxw
  if (num < 1000000) {
    const wan = num / 10000;
    return `${wan.toFixed(2)}w`;
  }

  // 100万到1亿之间，格式化为 xx.xxw
  if (num < 100000000) {
    const wan = num / 10000;
    return `${wan.toFixed(2)}w`;
  }

  // 超过1亿，格式化为 xx.xx亿
  const yi = num / 100000000;
  return `${yi.toFixed(2)}亿`;
};

/**
 * 格式化发布时间
 * 今天：09:30
 * 昨天：昨天 18:00
 * 前天：前天 14:30
 * 超过三天：2025/10/06 09:05
 * @param timestamp 时间，格式"2025-10-06 09:05:08"
 * @returns 格式化后的时间字符串
 */
export const formatTimePost = (timestamp: string): string => {
  if (!timestamp) {
    return '';
  }

  const now = dayjs();
  const postTime = dayjs(timestamp);
  
  // 使用 isSame 准确判断是否为同一天
  const isToday = now.isSame(postTime, 'day');
  const isYesterday = now.subtract(1, 'day').isSame(postTime, 'day');
  const isDayBeforeYesterday = now.subtract(2, 'day').isSame(postTime, 'day');

  // 格式化时间部分
  const timeStr = postTime.format('HH:mm');

  if (isToday) {
    // 今天只显示时间
    return timeStr;
  } 
  if (isYesterday) {
    // 昨天显示：昨天 18:00
    return `${i18next.t('time.yesterday')} ${timeStr}`;
  }
  if (isDayBeforeYesterday) {
    // 前天显示：前天 14:30
    return `${i18next.t('time.dayBeforeYesterday')} ${timeStr}`;
  }
  // 超过三天显示完整日期时间
  return postTime.format('YYYY/MM/DD HH:mm');
};